import { BufferGeometry, DataTexture, RGBAFormat, FloatType, NearestFilter } from 'three';
export function sampleSurface(g: BufferGeometry, count: number, bodyCount: number, eyeCenters: number[][], facialFeatures?: {mouthLine: number; mouthHalfWidth: number}) {
    const p = g.getAttribute('position'), n = g.getAttribute('normal'), index = g.index!, targets = g.morphAttributes.position!;
    const mouthLine=facialFeatures?.mouthLine??eyeCenters[0][1]-.84;
    const mouthWidth=facialFeatures?.mouthHalfWidth??.36;
    let state = 847321;
    const random = () => { state = (Math.imul(state, 1664525) + 1013904223) >>> 0; return state / 4294967296; };
    const detailAt = (x: number, y: number, z: number) => {
        if (z < .7) return 0;
        const e = eyeCenters[x > 0 ? 0 : 1];
        const eyeLine=e[1]+Math.max(0,Math.abs(x)-Math.abs(e[0]))*.10;
        const ring = Math.hypot((x - e[0]) / .195, (y - eyeLine) / .082);
        const eyelid = Math.exp(-Math.pow((ring - 1) / .23, 2))*(y>=eyeLine?1:.42);
        const browLine=e[1]+.14+.075*Math.exp(-Math.pow((Math.abs(x)-.49)/.16,2));
        const brow = Math.exp(-Math.pow((y - browLine) / .025, 2) - Math.pow((x - e[0]) / .245, 4));
        const lipSpan=Math.max(0,1-Math.pow(x/mouthWidth,2));
        const upperLip=mouthLine+.054*lipSpan+.018*Math.exp(-Math.pow((Math.abs(x)-.09)/.055,2));
        const lowerLip=mouthLine-.066*lipSpan;
        const lips=Math.max(Math.exp(-Math.pow((y-upperLip)/.028,2)),Math.exp(-Math.pow((y-lowerLip)/.032,2)))*Math.exp(-Math.pow(x/mouthWidth,6));
        const nose = Math.exp(-Math.pow(x / .115, 2) - Math.pow((y - e[1] + .38) / .36, 2));
        return Math.max(eyelid, brow, lips, nose * .5);
    };
    const cdf = new Float64Array(index.count / 3);
    let total = 0;
    for (let f = 0; f < cdf.length; f++) {
        const a = index.getX(f * 3), b = index.getX(f * 3 + 1), c = index.getX(f * 3 + 2);
        const ux = p.getX(b) - p.getX(a), uy = p.getY(b) - p.getY(a), uz = p.getZ(b) - p.getZ(a), vx = p.getX(c) - p.getX(a), vy = p.getY(c) - p.getY(a), vz = p.getZ(c) - p.getZ(a);
        const area = Math.hypot(uy * vz - uz * vy, uz * vx - ux * vz, ux * vy - uy * vx) * .5;
        const x = (p.getX(a) + p.getX(b) + p.getX(c)) / 3, y = (p.getY(a) + p.getY(b) + p.getY(c)) / 3, z = (p.getZ(a) + p.getZ(b) + p.getZ(c)) / 3;
        const face = z > .65 && Math.abs(x) < .7 && y < .55 && y > -.95;
        const ear = Math.abs(x) > .8 && y < .4 && y > -.5;
        const inside = z < 1.22 && z > .75 && Math.abs(x) < .26 && y < -.45 && y > -.85;
        const eyeball = a>=bodyCount&&b>=bodyCount&&c>=bodyCount;
        const eye=eyeCenters[x>0?0:1];
        // Sample the visible globe cap generously, so an eye reads as an iris
        // and sclera within the real lids rather than an empty luminous ring.
        const globeWeight=eyeball&&z>eye[2]+.07?8:0;
        total += area * (inside ? .2 : (face ? 1.8 : ear ? 1.65 : .7) + detailAt(x, y, z) * 6 + globeWeight);
        cdf[f] = total;
    }
    const position = new Float32Array(count * 3), normal = new Float32Array(count * 3), seed = new Float32Array(count), tone = new Float32Array(count), detail = new Float32Array(count), faceIndex = new Uint32Array(count), barycentric = new Float32Array(count * 3), ids = new Float32Array(count);
    const width = 256, rows = Math.ceil(count / width), height = rows * targets.length;
    const delta = new Float32Array(width * height * 4);
    for (let i = 0; i < count; i++) {
        const r = random() * total;
        let lo = 0, hi = cdf.length - 1;
        while (lo < hi) {
            const mid = (lo + hi) >>> 1;
            if (cdf[mid] < r)
                lo = mid + 1;
            else
                hi = mid;
        }
        const f = lo, a = index.getX(f * 3), b = index.getX(f * 3 + 1), c = index.getX(f * 3 + 2), root = Math.sqrt(random()), w = [1 - root, root * (1 - random()), 0];
        w[2] = 1 - w[0] - w[1];
        const v = [a, b, c];
        faceIndex[i] = f;
        ids[i] = i;
        seed[i] = random();
        barycentric.set(w, i * 3);
        for (let k = 0; k < 3; k++) {
            position[i * 3 + k] = v.reduce((s, id, j) => s + p.array[id * 3 + k] * w[j], 0);
            normal[i * 3 + k] = v.reduce((s, id, j) => s + n.array[id * 3 + k] * w[j], 0);
        }
        detail[i]=detailAt(position[i * 3],position[i * 3 + 1],position[i * 3 + 2]);
        let t = .65 + random() * .25 + detail[i] * .85;
        // Brows and upper lip in the reference are darker anatomical accents,
        // not luminous horizontal plaques. Density still describes their shape.
        const px=position[i*3],py=position[i*3+1],pz=position[i*3+2],e=eyeCenters[px>0?0:1];
        if(pz>.7){
            const browLine=e[1]+.14+.075*Math.exp(-Math.pow((Math.abs(px)-.49)/.16,2));
            const brow=Math.exp(-Math.pow((py-browLine)/.026,2)-Math.pow((px-e[0])/.245,4));
            const span=Math.max(0,1-Math.pow(px/mouthWidth,2));
            const upper=mouthLine+.054*span+.018*Math.exp(-Math.pow((Math.abs(px)-.09)/.055,2));
            const lip=Math.exp(-Math.pow((py-upper)/.04,2)-Math.pow(px/mouthWidth,6));
            t*=1-Math.max(brow*.63,lip*.52);
        }
        if (a >= bodyCount && b >= bodyCount && c >= bodyCount) {
            detail[i]=1;
            const e = eyeCenters[position[i * 3] > 0 ? 0 : 1];
            const d = Math.hypot(position[i * 3] - e[0], position[i * 3 + 1] - e[1]);
            t = d < .041 ? .06 : d < .078 ? .54 : d < .089 ? .36 : .92;
        }
        tone[i] = t;
        for (let j = 0; j < targets.length; j++)
            for (let k = 0; k < 3; k++)
                delta[(j * rows * width + i) * 4 + k] = v.reduce((s, id, l) => s + targets[j].array[id * 3 + k] * w[l], 0);
    }
    const texture = new DataTexture(delta, width, height, RGBAFormat, FloatType);
    texture.minFilter = texture.magFilter = NearestFilter;
    texture.needsUpdate = true;
    return { position, normal, seed, tone, detail, ids, faceIndex, barycentric, texture, width, height, rows, count };
}
